/**
 * usePlan — single source of truth voor plan-state en entitlements.
 *
 * Plan states (canonical):
 *   - free_scan
 *   - self_service_connected_14_95
 *   - managed_ai_launch_month_995_95
 *   - managed_ai_recurring_2495_95
 *
 * Gebruik dit hook OVERAL i.p.v. `user?.role === 'radar_paid'` checks.
 * Dat soort role-checks zijn fragiel en houden geen rekening met
 * billing_phase of admin-role mappings.
 *
 * Voorbeeld:
 *   const plan = usePlan();
 *   if (plan.isManaged) { ... }
 *   if (plan.canExecuteManaged) { ... }
 *   {plan.shouldShowUpgradeNudge && <UpgradeNudge />}
 */
import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

export const PLAN_LABELS = {
  free_scan: 'Gratis Scan',
  self_service_connected_14_95: 'Connected Self-Service',
  managed_ai_launch_month_995_95: 'Managed AI · Launch Month',
  managed_ai_recurring_2495_95: 'Managed AI · Recurring',
};

export const PLAN_PRICE_LABELS = {
  free_scan: 'Gratis',
  self_service_connected_14_95: '€14,95/mnd excl. btw',
  managed_ai_launch_month_995_95: '€995,95 excl. btw (eerste maand)',
  managed_ai_recurring_2495_95: '€2.495,95/mnd excl. btw',
};

// Fallback mapping van legacy `role` → canonical plan_state.
// Wordt alleen gebruikt als de backend `plan_state` (nog) niet meestuurt.
const ROLE_FALLBACK = {
  radar_free: 'free_scan',
  radar_paid: 'self_service_connected_14_95',
  beheer_paid: 'managed_ai_recurring_2495_95',
  admin: 'managed_ai_recurring_2495_95',
  manager: 'managed_ai_recurring_2495_95',
};

export default function usePlan() {
  const { user } = useAuth();

  return useMemo(() => {
    // Single source: prefer backend-injected plan_state
    let planState = user?.plan_state;
    if (!planState) {
      planState = ROLE_FALLBACK[user?.role] || 'free_scan';
      // Backend stuurt billing_phase mee; gebruik dat om launch_month nauwkeurig te detecteren
      if (planState === 'managed_ai_recurring_2495_95' && user?.billing_phase === 'launch_month') {
        planState = 'managed_ai_launch_month_995_95';
      }
    }

    const isFree = planState === 'free_scan';
    const isSelfService = planState === 'self_service_connected_14_95';
    const isLaunchMonth = planState === 'managed_ai_launch_month_995_95';
    const isRecurring = planState === 'managed_ai_recurring_2495_95';
    const isManaged = isLaunchMonth || isRecurring;
    const isPaid = !isFree;

    // Entitlements (backend stuurt deze ook mee; fallback hier voor offline UI)
    const ent = user?.entitlements || {};
    const supportsIntegrations = ent.supports_integrations ?? !isFree;
    const supportsAiExecution = ent.supports_ai_execution ?? isManaged;
    const supportsManagedExecution = ent.supports_managed_execution ?? isManaged;

    // Upgrade-nudge regels — premium gebruikers zien NOOIT een lower-tier nudge
    const shouldShowUpgradeToSelfService = isFree;
    const shouldShowUpgradeToPremium = isFree || isSelfService;
    const shouldShowAnyUpgrade = shouldShowUpgradeToSelfService || shouldShowUpgradeToPremium;

    // Execution mode rules — wat de UI mag tonen als "actie"
    // suggest_only: alleen tonen wat er beter kan
    // guided: copy-paste instructies + 1-klik kopie
    // one_click: API-call doet de wijziging
    // managed: IMPERIA AI team + bot voert uit
    const allowedExecutionModes = isManaged
      ? ['suggest_only', 'guided', 'one_click', 'managed']
      : isSelfService
        ? ['suggest_only', 'guided']
        : ['suggest_only'];

    return {
      planState,
      planLabel: user?.plan_label || PLAN_LABELS[planState] || 'Onbekend',
      priceLabel: PLAN_PRICE_LABELS[planState] || '',
      role: user?.role,
      billingPhase: user?.billing_phase,
      // Tier flags
      isFree,
      isSelfService,
      isLaunchMonth,
      isRecurring,
      isManaged,
      isPaid,
      // Entitlements
      supportsIntegrations,
      supportsAiExecution,
      supportsManagedExecution,
      allowedExecutionModes,
      canExecute: (mode) => allowedExecutionModes.includes(mode),
      // Upgrade rules
      shouldShowUpgradeToSelfService,
      shouldShowUpgradeToPremium,
      shouldShowAnyUpgrade,
    };
  }, [user]);
}
